import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { differenceInDays } from 'date-fns';
import { ConfigBaseFilter, ConfigService, ListLayout, ListLayoutMultipleComponent, ListSortOption, SimpleConfigBaseFilter, SkeletonLoaderLayout } from 'gung-list';
import { Observable, map } from 'rxjs';
import { NewsEditorTableComponent } from '../../../components/news/news-editor-table/news-editor-table.component';
import { News } from '../../../models/news';
import { NewsService } from '../news.service';

@Injectable({
  providedIn: 'root'
})
export class NewsEditorConfigListService implements ConfigService<News> {
  protected newsService = inject(NewsService);
  protected translateService = inject(TranslateService);

  skeletonLoaderLayout: SkeletonLoaderLayout = 'table';

  getItems(): Observable<News[]> {
    // Pinned news first, in their pinOrder, so the admin sees them as the readers will.
    return this.newsService.getAllNews().pipe(map(news => this.newsService.sortPinnedFirst(news)));
  }

  getFilters(): ConfigBaseFilter<News>[] {
    return [new NewsDateFilter(this.translateService), new NewsStatusFilter(), new NewsCategoryFilter()];
  }

  getSortOptions(): ListSortOption<News>[] {
    return [];
  }

  getBatchSizes(): number[] {
    return [24];
  }

  getLayouts(): ListLayout<News>[] {
    return [
      {
        getIconClass: () => '',
        getListItemComponent: () => NewsEditorTableComponent,
        getListLayoutComponent: () => ListLayoutMultipleComponent,
        getName: () => 'NewsTable'
      }
    ];
  }

  getSearchTerms(item: News): string[] {
    return [item.id, item.name];
  }

  getItemId(item: News): string {
    return item.id;
  }

  getNothingFoundTranslateTag() {
    return 'NOTHING_FOUND';
  }
}

export class NewsDateFilter extends SimpleConfigBaseFilter<News> {
  constructor(protected translateService: TranslateService) {
    super();
  }

  getName(): string {
    return 'DATE';
  }

  getOptionIds(item: News): string[] {
    const dayDifferense = differenceInDays(new Date(), new Date(item.extra.timestamp));

    if (dayDifferense < 1) {
      return ['TODAY'];
    }
    if (dayDifferense < 7) {
      return ['THIS_WEEK'];
    }
    if (dayDifferense < 30) {
      return ['THIS_MONTH'];
    }
    if (dayDifferense < 90) {
      return ['LAST_THREE_MONTHS'];
    }
    return ['OVER_THREE_MONTHS'];
  }

  getOptionName(key: string): string {
    return this.translateService.instant(key);
  }
}

export class NewsStatusFilter extends SimpleConfigBaseFilter<News> {
  getName(): string {
    return 'ACTIVE';
  }

  getOptionIds(item: News): string[] {
    return [item.active ? 'YES' : 'NO'];
  }

  getOptionName(key: string): string {
    return key;
  }
}

export class NewsCategoryFilter extends SimpleConfigBaseFilter<News> {
  getName(): string {
    return 'CATEGORY';
  }

  getOptionIds(item: News): string[] {
    return [item.extra?.category || 'UNDEFINED'];
  }

  getOptionName(key: string): string {
    return key;
  }
}